import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isNum, fx, fmtN, sgn, md, parseIso, isoOf, niceMax } from './analytics/format';

// 季節・時系列分析。形は依頼者の画面案のとおり（今年と昨年の動き・季節カレンダー）。
// 集計はサーバー（GbpAnalytics::SeasonTrend）で、この画面は条件を URL に入れて season_data を読み直し、
// 週ごとの平均を取って描くだけ。
// 画面案の「立地」（データが無い）・「丸数字は分析メモ」（分析メモが保留）・
// 「外部要因の影響の大きさ」「説明のつかない大きな動き」（作らないと決めた）は無い。

const KEYS = ['ind', 'pref', 'metric'];
const DEFAULTS = { ind: '', pref: '', metric: 'uu' };
const THIS_COLOR = '#343a40';
const LAST_COLOR = '#adb5bd';

const toQuery = (st) => KEYS.filter((k) => st[k] && String(st[k]) !== DEFAULTS[k]).map((k) => `${k}=${encodeURIComponent(st[k])}`).join('&');
const addDays = (iso, n) => { const d = parseIso(iso); d.setUTCDate(d.getUTCDate() + n); return isoOf(d); };

// 1月1日から7日ずつ区切った53週の平均（サーバーの CSV と同じ区切り）
function weekly(daily, year) {
  const out = [];
  for (let w = 0; w < 53; w += 1) {
    const first = addDays(`${year}-01-01`, w * 7);
    let sum = 0; let n = 0;
    for (let i = 0; i < 7; i += 1) {
      const d = addDays(first, i);
      if (d.slice(0, 4) !== String(year)) break;
      if (isNum(daily[d])) { sum += daily[d]; n += 1; }
    }
    out.push(n ? sum / n : null);
  }
  return out;
}

// 年平均＝100 からの上下で色を付ける（青＝多い月・オレンジ＝少ない月）
const calBg = (v) => {
  if (!isNum(v)) return 'transparent';
  const a = (0.08 + 0.5 * Math.min(1, Math.abs(v - 100) / 25)).toFixed(2);
  return v >= 100 ? `rgba(42,120,214,${a})` : `rgba(235,104,52,${a})`;
};

export default function SeasonTrend(props) {
  const { industries, prefs, metrics, urls, outOfRank = 21 } = props;
  const [st, setSt] = useState(() => {
    const init = { ...DEFAULTS };
    KEYS.forEach((k) => { if (props.initial && props.initial[k] !== undefined) init[k] = String(props.initial[k]); });
    return init;
  });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const seq = useRef(0);

  const query = toQuery(st);
  useEffect(() => {
    try { window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`); } catch (e) { /* 履歴を書けなくても画面は動く */ }
    const my = ++seq.current;
    setLoading(true);
    fetch(`${urls.data}?${query}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((json) => { if (my === seq.current) { setRes(json); setError(''); setLoading(false); } })
      .catch(() => { if (my === seq.current) { setError('読み込めませんでした。画面を読み込み直してください'); setLoading(false); } });
  }, [query]);

  const set = (patch) => setSt((s) => ({ ...s, ...patch }));
  const sel = (label, key, options) => (
    <label className="sd-ctl">{label}
      <select value={st[key]} onChange={(e) => set({ [key]: e.target.value })}>
        {options.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select>
    </label>
  );

  const isRank = st.metric === 'kw1';
  const metricLabel = (metrics.find((m) => m.key === st.metric) || {}).label;
  const year = res ? res.year : null;
  const series = res ? { now: weekly(res.thisYear, res.year), last: weekly(res.lastYear, res.year - 1) } : null;
  const empty = res && Object.keys(res.thisYear).length + Object.keys(res.lastYear).length === 0;
  const msg = error || (!res ? '読み込み中…' : empty ? 'この条件のデータがありません。業種か地域を「すべて」に戻してください' : null);

  return (
    <div className="sd ef se">
      <div className="sd-card ef-ctl">
        <div className="ef-row">
          {sel('業種', 'ind', [['', 'すべて'], ...industries.map((o) => [String(o.id), o.label])])}
          {sel('地域', 'pref', [['', 'すべて'], ...prefs.map((o) => [String(o.id), o.label])])}
          {sel('見る数字', 'metric', metrics.map((m) => [m.key, m.label]))}
          {loading && res && <span className="ef-loading">読み込み中…</span>}
          <span className="sc-sep" />
          <a className="sd-btn sd-small" href={`${urls.csv}${query ? `?${query}` : ''}`}>⤓ CSV</a>
        </div>
      </div>

      <div className={`ef-stack${loading && res ? ' ef-dim' : ''}`}>
        <div className="sd-card">
          <header>
            <h2>今年と昨年の動き{res ? `（${res.label} ・ ${res.stores}店）` : ''}</h2>
            <span className="sd-sub">{metricLabel} ・ 1店舗あたりの1日平均（週ごと）</span>
          </header>
          <div className="sd-legend">
            <span><i className="sd-lg-line" style={{ background: THIS_COLOR }} />{year ? `${year}年` : '今年'}</span>
            <span><i className="sd-lg-line" style={{ background: LAST_COLOR }} />{year ? `${year - 1}年` : '昨年'}</span>
            {isRank && <span>順位は1位が上</span>}
          </div>
          <div className="sd-body">
            <YoyChart series={series} year={year || new Date().getFullYear()} isRank={isRank} outOfRank={outOfRank} msg={msg} />
          </div>
        </div>

        <div className="sd-card">
          <header>
            <h2>季節カレンダー</h2>
            <span className="sd-sub">業種ごとの月の上下（年平均＝100）{res ? ` ・ ${res.calendar.metricLabel}` : ''}</span>
          </header>
          <div className="sd-body">
            {res ? <Calendar cal={res.calendar} selected={st.ind} isRank={isRank} /> : <p className="sd-note">{msg}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- 今年と昨年の動き（SVG） -------------------------------------------------
// 幅は枠に合わせて測る（viewBox を固定すると、枠が広いとき文字まで拡大されるため）。

function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(900);
  useLayoutEffect(() => {
    const measure = () => { const el = ref.current; if (el && el.clientWidth) setW(Math.max(360, Math.round(el.clientWidth))); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return [ref, w];
}

function YoyChart({ series, year, isRank, outOfRank, msg }) {
  const [ref, W] = useWidth();
  const ml = 56; const mr = 96; const x0 = ml; const x1 = W - mr;
  const t = 12; const bot = 250; const H = bot + 28;
  const X = (w) => x0 + (w / 52) * (x1 - x0);

  const now = series ? series.now : [];
  const last = series ? series.last : [];
  const all = now.concat(last).filter(isNum);
  let lo; let hi;
  if (isRank) {
    lo = all.length ? Math.max(1, Math.floor(Math.min(...all)) - 1) : 1;
    hi = all.length ? Math.min(outOfRank, Math.ceil(Math.max(...all)) + 1) : outOfRank;
    if (hi <= lo) hi = lo + 1;
  } else {
    lo = 0;
    hi = niceMax(Math.max(1, ...all) * 1.1);
  }
  // 順位は1位が上
  const Y = isRank ? (v) => t + (bot - t) * ((v - lo) / (hi - lo)) : (v) => t + (bot - t) * (1 - (v - lo) / (hi - lo));
  const ticks = isRank
    ? (() => { const step = Math.max(1, Math.ceil((hi - lo) / 4)); const out = []; for (let v = lo; v <= hi; v += step) out.push(v); return out; })()
    : (() => {
      // 目盛りは 1・2・2.5・5 の10のべき倍で、4〜5本になる刻み（37.5 のような半端を出さない）
      const raw = hi / 4; const p10 = 10 ** Math.floor(Math.log10(raw));
      const step = [1, 2, 2.5, 5, 10].map((k) => k * p10).find((v) => v >= raw);
      const out = []; for (let v = 0; v <= hi + 1e-9; v += step) out.push(v);
      return out;
    })();
  const fmtV = (v) => (isRank ? `${fx(v, 1)}位` : hi < 10 ? fx(v, 2) : fx(v, 1));
  const fmtTick = (v) => (isRank ? (v >= outOfRank ? '圏外' : `${v}位`) : hi < 10 ? fx(v, 1) : fmtN(v));

  const path = (arr) => {
    let p = ''; let pen = false;
    arr.forEach((v, w) => { if (!isNum(v)) { pen = false; return; } p += `${pen ? 'L' : 'M'}${X(w).toFixed(1)} ${Y(v).toFixed(1)}`; pen = true; });
    return p;
  };
  const lastIdx = (arr) => { let i = arr.length - 1; while (i >= 0 && !isNum(arr[i])) i -= 1; return i; };
  const iNow = lastIdx(now); const iLast = lastIdx(last);
  // 右端の名前。近いときは上下にずらして重ならないようにする
  let yNow = iNow >= 0 ? Y(now[iNow]) : null; let yLast = iLast >= 0 ? Y(last[iLast]) : null;
  if (yNow !== null && yLast !== null && Math.abs(yNow - yLast) < 14) {
    const mid = (yNow + yLast) / 2;
    if (yNow <= yLast) { yNow = mid - 7; yLast = mid + 7; } else { yNow = mid + 7; yLast = mid - 7; }
  }

  const months = Array.from({ length: 12 }, (_, m) => {
    const days = Math.round((Date.UTC(year, m, 1) - Date.UTC(year, 0, 1)) / 86400000);
    return { m: m + 1, x: X(days / 7) };
  });

  return (
    <div ref={ref}>
      <svg className="sd-chart" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="今年と昨年の週ごとの推移">
        {ticks.map((v) => (
          <g key={v}>
            <line className="sd-grid" x1={x0} x2={x1} y1={Y(v)} y2={Y(v)} />
            <text x={x0 - 6} y={Y(v) + 4} textAnchor="end">{fmtTick(v)}</text>
          </g>
        ))}
        {months.map((o) => <text key={o.m} x={o.x} y={H - 8} textAnchor="start">{o.m}月</text>)}
        <path d={path(last)} fill="none" stroke={LAST_COLOR} strokeWidth="2" strokeLinejoin="round" />
        <path d={path(now)} fill="none" stroke={THIS_COLOR} strokeWidth="2.4" strokeLinejoin="round" />
        {iNow >= 0 && <circle cx={X(iNow)} cy={Y(now[iNow])} r="3.5" fill={THIS_COLOR} />}
        {iNow >= 0 && <text x={x1 + 8} y={yNow + 4} className="sd-t-ink2" style={{ fill: THIS_COLOR, fontWeight: 700 }}>{`${year}年 ${fmtV(now[iNow])}`}</text>}
        {iLast >= 0 && <text x={x1 + 8} y={yLast + 4} className="sd-t-ink2" style={{ fill: '#868e96', fontWeight: 700 }}>{`${year - 1}年 ${fmtV(last[iLast])}`}</text>}
        {msg && <text x={(x0 + x1) / 2} y={(t + bot) / 2} textAnchor="middle" className="sc-empty-t">{msg}</text>}
        {series && now.map((v, w) => {
          const first = isoOf(new Date(Date.UTC(year, 0, 1 + w * 7)));
          const l = last[w];
          const diff = isNum(v) && isNum(l) ? (isRank ? `\n昨年との差 ${sgn(l - v, 1)}位（上がった＝プラス）` : l ? `\n昨年比 ${sgn((v / l - 1) * 100, 1)}%` : '') : '';
          const cw = (x1 - x0) / 52;
          return (
            <rect key={first} x={X(w) - cw / 2} y={t} width={cw} height={bot - t} fill="transparent">
              <title>{`${w + 1}週目（${md(first)}〜）\n${year}年 ${isNum(v) ? fmtV(v) : '—'}\n${year - 1}年 ${isNum(l) ? fmtV(l) : '—'}${diff}`}</title>
            </rect>
          );
        })}
      </svg>
    </div>
  );
}

// ---- 季節カレンダー ---------------------------------------------------------

function Calendar({ cal, selected, isRank }) {
  if (cal.rows.length === 0) return <p className="sd-note">この条件ではインサイトのある店舗がありません。</p>;
  return (
    <>
      <div className="sd-tbl-wrap">
        <table className="sd-table se-cal">
          <thead>
            <tr>
              <th>業種</th>
              <th className="sd-num">店舗数</th>
              {Array.from({ length: 12 }, (_, m) => <th key={m} className="se-c">{m + 1}月</th>)}
            </tr>
          </thead>
          <tbody>
            {cal.rows.map((row) => (
              <tr key={row.id} className={`${String(row.id) === String(selected) ? 'se-sel' : ''}${row.stores < cal.fewStores ? ' se-few' : ''}`}>
                <th>{row.name}</th>
                <td className="sd-num">{row.stores}店</td>
                {row.months.map((v, m) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <td key={m} className="se-c" style={{ background: calBg(v) }} title={`${row.name} ${m + 1}月：${isNum(v) ? v : '—'}（年平均＝100）`}>
                    {isNum(v) ? v : '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="sd-note se-note">
        青＝年平均より多い月 ／ オレンジ＝少ない月。インサイトは取り込みを始めた月からのため、月によっては1〜2年分の平均です。
        {cal.fewStores}店未満の業種は1店の事情で上下しやすいので薄く出しています。
        {isRank && ' KW1の順位を選んでいるときは、カレンダーは UU数 で出しています。'}
        {` 確定前のインサイトは入れていません（${cal.to.replace(/-/g, '/')}まで）。`}
      </p>
    </>
  );
}
